/**
 * ============================================================================
 * PAYMENT METHODS COMPONENT
 * ============================================================================
 * 
 * Screen 4: Payment Method Selection
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTransaction } from '../../hooks';
import { useI18n } from '../../providers';
import Button from '../common/Button';
import Card from '../common/Card';
import Spinner from '../common/Spinner';

/**
 * Payment Methods Component
 * @param {Object} props
 * @param {number} props.amount - Payment amount (optional; loaded from transaction if not provided)
 * @param {string} props.currency - Currency code
 * @param {string} props.transactionId - Transaction ID
 * @returns {JSX.Element}
 */
const PaymentMethods = ({ amount: amountProp, currency: currencyProp = 'XAF', transactionId }) => {
  const navigate = useNavigate();
  const { formatCurrency, language } = useI18n();
  const { getTransaction, loading: transactionLoading } = useTransaction();
  const [transaction, setTransaction] = useState(null);

  useEffect(() => {
    if (!transactionId) return;
    let cancelled = false;
    getTransaction(transactionId)
      .then((data) => {
        if (!cancelled) {
          const tx = data?.data?.transaction || data?.data || data;
          setTransaction(tx);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [transactionId, getTransaction]);

  const displayAmount = amountProp ?? transaction?.totalAmount ?? transaction?.amount ?? 0;
  const displayCurrency = currencyProp || transaction?.currency || 'XAF';
  const isLoading = transactionLoading || (transactionId && !transaction && !amountProp);

  const handleSelectMethod = (methodId) => {
    navigate(`/transactions/${transactionId}/pay/${methodId}`);
  };

  const paymentMethods = [
    {
      id: 'mtn',
      name: 'MTN Mobile Money',
      icon: '📱',
      description: language === 'fr' ? 'Argent mobile MTN (Cameroun)' : 'MTN Mobile Money (Cameroon)',
      popular: true,
      available: true,
      color: 'from-yellow-500 to-orange-500',
    },
    {
      id: 'orange',
      name: 'Orange Money',
      icon: '🍊',
      description: language === 'fr' ? 'Orange Money (Cameroun)' : 'Orange Money (Cameroon)',
      popular: true,
      available: true,
      color: 'from-orange-500 to-red-500',
    },
    {
      id: 'card',
      name: language === 'fr' ? 'Carte bancaire' : 'Card Payment',
      icon: '💳',
      description: language === 'fr' ? 'Carte de crédit/débit' : 'Credit/Debit card',
      popular: false,
      available: true,
      color: 'from-blue-500 to-purple-500',
    },
  ];

  return (
    <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6 transition-colors group"
          >
            <svg
              className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span className="font-medium">{language === 'fr' ? 'Retour' : 'Back'}</span>
          </button>
          <h1 className="text-3xl font-black bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent mb-2">
            {language === 'fr' ? 'Choisir le mode de paiement' : 'Choose Payment Method'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {language === 'fr' ? 'Sélectionnez votre mode de paiement préféré' : 'Select your preferred payment method'}
          </p>
        </div>

        {/* Amount Display */}
        {isLoading ? (
          <Card variant="glass" className="p-8 mb-8 text-center">
            <Spinner size="lg" />
          </Card>
        ) : (
          <Card variant="glass" className="p-8 mb-8 text-center animate-fadeInUp">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-2 font-medium">
              {language === 'fr' ? 'Payer' : 'Pay'}
            </div>
            <div className="text-4xl font-black bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
              {formatCurrency(displayAmount, displayCurrency)}
            </div>
          </Card>
        )}

        {/* Payment Methods */}
        <div className="space-y-4 mb-8">
          {paymentMethods.map((method, index) => (
            <Card
              key={method.id}
              variant="glass"
              hover
              clickable
              onClick={() => method.available && handleSelectMethod(method.id)}
              className={`
                ${!method.available ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                animate-fadeInUp
                ${method.popular ? 'ring-2 ring-blue-500/50' : ''}
              `}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl shadow-lg ${
                    method.popular 
                      ? `bg-gradient-to-br ${method.color || 'from-blue-500 to-green-500'}` 
                      : 'bg-gray-100 dark:bg-gray-700'
                  }`}>
                    {method.icon}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-black text-gray-900 dark:text-white">
                        {method.name}
                      </h3>
                      {method.popular && (
                        <span className="px-2.5 py-1 bg-gradient-to-r from-blue-600 to-green-600 text-white text-xs font-bold rounded-lg shadow-md">
                          {language === 'fr' ? 'Populaire' : 'Popular'}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {method.description}
                    </p>
                  </div>
                </div>
                <svg
                  className="w-6 h-6 text-gray-400 transition-colors"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </Card>
          ))}
        </div>

        {/* Security Badge */}
        <Card variant="glass" className="text-center animate-fadeInUp" style={{ animationDelay: '0.4s' }}>
          <div className="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400">
            <span className="text-xl">🔒</span>
            <p className="text-sm font-medium">
              {language === 'fr' ? 'Paiement sécurisé via WekaTrust Escrow' : 'Secure payment via WekaTrust Escrow'}
            </p>
          </div>
        </Card>
    </div>
  );
};

export default PaymentMethods;
